import { useEffect, useState } from 'react';

/**
 * Is this a phone — not a tablet, not a narrow desktop window?
 *
 * There is no single reliable "is this a phone" signal, so this combines three
 * weak ones and requires all of them to agree. Every judgment call below is
 * biased the same way: **when unsure, say no.** A phone user who misses the
 * notice has a slightly rough afternoon; a laptop user who gets told to switch
 * to a computer looks at a product that does not know what it is running on.
 *
 * The three signals:
 *
 *   1. `userAgentData.mobile` — Chromium's own answer, and the best one where
 *      it exists. It is false on Android tablets, which is exactly the
 *      distinction that is otherwise hard to make. Absent on Safari and
 *      Firefox, so it can only be trusted as a *negative*.
 *   2. The user-agent string. Android tablets omit the `Mobile` token that
 *      Android phones carry, which is the long-standing way to tell them apart.
 *   3. The physical screen's short edge, and a touch-first pointer.
 *
 * Why the screen check earns its place: viewport width alone would fire on a
 * desktop browser resized narrow, which is a normal thing to do and not a
 * phone. `screen` describes the device rather than the window, so a half-width
 * window on a 27-inch monitor still reports 2560.
 *
 * Where this is knowingly wrong:
 *
 *   - **iPads never match**, because iPadOS has reported itself as a Mac since
 *     version 13. That is the desired answer here, and it is luck rather than
 *     design — worth knowing if this is ever reused to detect tablets.
 *   - **A phone with "Request Desktop Website" on** also reports as a Mac and
 *     will not see the notice. Failing quiet, and they asked for it.
 *   - **An unfolded foldable** is over the size threshold and will not see it.
 *     Its screen really is tablet-sized, so that is arguably right.
 */

/** Phones, and only phones. Android tablets have no `Mobile` token. */
const PHONE_UA = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|BB10|Opera Mini|IEMobile/i;

/**
 * CSS pixels on the short edge, above which a device is not a phone.
 *
 * The largest phone in circulation is 440 wide; the smallest tablet, the iPad
 * mini, is 744. The gap is wide enough that the exact number barely matters.
 */
const PHONE_MAX_SHORT_EDGE = 500;

/** Not in TypeScript's DOM lib yet — Chromium-only, and unavailable elsewhere. */
interface NavigatorUA {
  userAgentData?: { mobile?: boolean };
}

export interface DeviceSignals {
  /** `navigator.userAgentData.mobile`, or undefined off Chromium. */
  hinted: boolean | undefined;
  userAgent: string;
  /** The short edge of the *screen*, in CSS pixels — not the window. */
  screenShortEdge: number;
  coarsePointer: boolean;
}

/**
 * The decision, separated from reading the browser.
 *
 * Pure so it can be checked against a table of real devices rather than only by
 * picking up a phone — which is the one way of testing this that does not scale
 * to the devices we do not own.
 */
export function isPhoneEnvironment({
  hinted,
  userAgent,
  screenShortEdge,
  coarsePointer,
}: DeviceSignals): boolean {
  // Where the browser answers directly, that answer wins in both directions.
  if (hinted === false) return false;
  if (hinted !== true && !PHONE_UA.test(userAgent)) return false;

  // The device's screen, not the window: a narrow desktop window is still a
  // desktop, and this is the check that knows the difference.
  if (screenShortEdge > PHONE_MAX_SHORT_EDGE) return false;

  // A last guard against a spoofed or unusual user agent on a real computer.
  return coarsePointer;
}

export function detectPhone(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  return isPhoneEnvironment({
    hinted: (navigator as Navigator & NavigatorUA).userAgentData?.mobile,
    userAgent: navigator.userAgent,
    screenShortEdge: Math.min(window.screen.width, window.screen.height),
    coarsePointer: window.matchMedia('(pointer: coarse)').matches,
  });
}

/**
 * Evaluated once on mount rather than watched.
 *
 * Nothing here changes while the page is open — rotating a phone does not make
 * it a laptop — and re-running on resize would risk the notice appearing partway
 * through a session, which is worse than not appearing at all.
 */
export function useIsPhone(): boolean {
  const [isPhone] = useState(detectPhone);
  return isPhone;
}

const SEEN_KEY = 'cg.mobileNoticeSeen';

export function hasSeenMobileNotice(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // Site data blocked. Showing it again is the safer of the two mistakes.
    return false;
  }
}

export function rememberMobileNotice(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Site data blocked. It reappears next visit, which is only a nuisance.
  }
}

/** True once, per browser: the notice is information, not a gate. */
export function useMobileNotice(): { open: boolean; dismiss: () => void } {
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isPhone && !hasSeenMobileNotice()) setOpen(true);
  }, [isPhone]);

  return {
    open,
    dismiss: () => {
      rememberMobileNotice();
      setOpen(false);
    },
  };
}
